import {
  type GraphParameters,
  isPathLookupRequestParameters,
  isTopologyRequestParameters,
  safeAbsurd,
} from '@ipfabric/utils';
import type { AppTranslations } from 'translations/translations';

import { getCompareModeBreadcrumbText } from '../breadcrumbTypes/CompareModeBreadcrumbItem';
import { getIntentCheckBreadcrumbText } from '../breadcrumbTypes/IntentCheckBreadcrumbItem';
import { getLoadedViewBreadcrumbText } from '../breadcrumbTypes/LoadedViewBreadcrumbItem';
import { getNetworkPathBreadcrumbText } from '../breadcrumbTypes/NetworkPathBreadcrumbItem';
import { getNetworkViewerModeBreadcrumbText } from '../breadcrumbTypes/NetworkViewerModeBreadcrumbItem';
import { getPathLookupInstanceBreadcrumbText } from '../breadcrumbTypes/PathLookupInstanceBreadcrumbItem';
import { getPathLookupModeBreadcrumbText } from '../breadcrumbTypes/PathLookupModeBreadcrumbItem';
import type { GraphBreadcrumb, GraphBreadcrumbType } from './buildBreadcrumbs';

const breadcrumbsFunctionByType: {
  [Key in GraphBreadcrumbType]: (
    breadcrumb: GraphBreadcrumb & { type: Key },
    msg: AppTranslations,
  ) => string;
} = {
  compareMode: getCompareModeBreadcrumbText,
  intentCheck: getIntentCheckBreadcrumbText,
  loadedView: getLoadedViewBreadcrumbText,
  networkViewer: getNetworkViewerModeBreadcrumbText,
  path: getNetworkPathBreadcrumbText,
  pathLookup: getPathLookupModeBreadcrumbText,
  pathLookupInstance: getPathLookupInstanceBreadcrumbText,
};

export const getTextBreadcrumbs = (
  breadcrumbs: GraphBreadcrumb[],
  msg: AppTranslations,
): string | null => {
  if (breadcrumbs.length === 0) {
    return null;
  }

  return breadcrumbs
    .map(
      // Type consistency of breadcrumb is guaranteed by breadcrumbsFunctionByType.
      (breadcrumb) =>
        (breadcrumbsFunctionByType[breadcrumb.type] as any)(breadcrumb, msg),
    )
    .join(' / ');
};

export const getBreadcrumbsShortLabel = (
  parameters: GraphParameters,
  msg: AppTranslations,
): string | null => {
  const {
    canvasGraph: { breadcrumbs: breadcrumbsMsg },
  } = msg;

  if (isPathLookupRequestParameters(parameters)) {
    return breadcrumbsMsg.pathLookupMode.shortLabel[parameters.pathLookupType];
  }
  if (isTopologyRequestParameters(parameters)) {
    return breadcrumbsMsg.networkViewerMode.groupedBy(parameters.groupBy);
  }
  safeAbsurd(parameters, 'unknown parameters type');
  return null;
};
